import { useState, useMemo } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  useColorMode,
  Icon,
  Badge,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Divider,
  Alert,
  AlertIcon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  useToast,
  Tooltip,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Code,
  IconButton,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../brandConfig";
import {
  FaWallet,
  FaCoins,
  FaCopy,
  FaCheckCircle,
  FaInfoCircle,
  FaChartLine,
  FaExchangeAlt,
  FaClock,
  FaFire,
  FaServer,
  FaUsers,
  FaBalanceScale,
} from "react-icons/fa";
import { GiTwoCoins } from "react-icons/gi";

// Contract addresses
const TREASURY_ADDRESS = "0x86aa6Ac064b556c2CF38e5c97986423B2ee4e6Ee";
const RIO_TOKEN_ADDRESS = "0xB5d0d9b305F6DF9D0769a14C45d8113c664e520f";
const USDC_BASE_ADDRESS = "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913";

// Sale parameters
const TOTAL_SUPPLY = 52_000_000;
const START_RATE = 1.4; // tokens per USDC at start
const END_RATE = 0.6; // tokens per USDC at end

// Calculate current rate based on tokens sold
const calculateRate = (tokensSold: number): number => {
  const progress = tokensSold / TOTAL_SUPPLY;
  return START_RATE - (START_RATE - END_RATE) * progress;
};

// Calculate tokens received for a given USDC amount at current rate
const calculateTokensReceived = (usdcAmount: number, tokensSold: number): number => {
  const rate = calculateRate(tokensSold);
  return usdcAmount * rate;
};

export const TokenSalePage = () => {
  const { colorMode } = useColorMode();
  const toast = useToast();

  // Sale state - this would come from a backend/blockchain in production
  const [tokensSold, setTokensSold] = useState(0); // Start at 0 for demo

  // Input state
  const [usdcAmount, setUsdcAmount] = useState("");
  const [inputMode, setInputMode] = useState<"usdc" | "tokens">("usdc");
  const [tokenAmount, setTokenAmount] = useState("");
  const [copied, setCopied] = useState(false);

  // Calculate current values
  const currentRate = useMemo(() => calculateRate(tokensSold), [tokensSold]);
  const saleProgress = (tokensSold / TOTAL_SUPPLY) * 100;

  // Calculate based on input
  const calculatedTokens = useMemo(() => {
    const usdc = parseFloat(usdcAmount) || 0;
    return calculateTokensReceived(usdc, tokensSold);
  }, [usdcAmount, tokensSold]);

  // Copy address to clipboard
  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(TREASURY_ADDRESS);
      setCopied(true);
      toast({
        title: "Address Copied",
        description: "Treasury address copied to clipboard",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: "Copy Failed",
        description: "Please copy the address manually",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  // Price examples at different stages
  const priceExamples = [
    { sold: 0, label: "Start", rate: calculateRate(0) },
    { sold: 13_000_000, label: "25%", rate: calculateRate(13_000_000) },
    { sold: 26_000_000, label: "50%", rate: calculateRate(26_000_000) },
    { sold: 39_000_000, label: "75%", rate: calculateRate(39_000_000) },
    { sold: 52_000_000, label: "End", rate: calculateRate(52_000_000) },
  ];

  return (
    <>
      <NavbarWithCallToAction />
      <Box
        minH="100vh"
        bg="#0a0a0f"
        color={getColor("text.primary", colorMode)}
      >
        <Container maxW="900px" py={20}>
          <VStack spacing={10} align="stretch">
            {/* Header */}
            <VStack spacing={4} textAlign="center">
              <Badge
                px={4}
                py={2}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                borderRadius="full"
                fontSize="sm"
                fontWeight="bold"
              >
                PRE-SALE LIVE
              </Badge>
              <Heading
                size="2xl"
                color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}
              >
                B52 Token Pre-Sale
              </Heading>
              <Text
                fontSize="xl"
                color={getColor("text.secondary", colorMode)}
                maxW="700px"
              >
                Get RIO tokens now at early-bird rates. RIO tokens will be swapped 1:1 for real B52 tokens at mainnet launch.
              </Text>
            </VStack>

            {/* Key Stats */}
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Box
                p={4}
                bg={colorMode === "dark" ? "rgba(16, 185, 129, 0.1)" : "green.50"}
                borderRadius="lg"
                border="1px solid"
                borderColor="#10b981"
                textAlign="center"
              >
                <Stat size="sm">
                  <StatLabel color={getColor("text.secondary", colorMode)} fontSize="xs">Current Rate</StatLabel>
                  <StatNumber color="#10b981" fontSize="xl">
                    {currentRate.toFixed(2)}
                  </StatNumber>
                  <StatHelpText color={getColor("text.secondary", colorMode)} fontSize="xs">tokens/USDC</StatHelpText>
                </Stat>
              </Box>
              <Box
                p={4}
                bg={colorMode === "dark" ? "rgba(59, 130, 246, 0.1)" : "blue.50"}
                borderRadius="lg"
                border="1px solid"
                borderColor="#3b82f6"
                textAlign="center"
              >
                <Stat size="sm">
                  <StatLabel color={getColor("text.secondary", colorMode)} fontSize="xs">Start Rate</StatLabel>
                  <StatNumber color="#3b82f6" fontSize="xl">
                    {START_RATE}
                  </StatNumber>
                  <StatHelpText color={getColor("text.secondary", colorMode)} fontSize="xs">tokens/USDC</StatHelpText>
                </Stat>
              </Box>
              <Box
                p={4}
                bg={colorMode === "dark" ? "rgba(239, 68, 68, 0.1)" : "red.50"}
                borderRadius="lg"
                border="1px solid"
                borderColor="#ef4444"
                textAlign="center"
              >
                <Stat size="sm">
                  <StatLabel color={getColor("text.secondary", colorMode)} fontSize="xs">End Rate</StatLabel>
                  <StatNumber color="#ef4444" fontSize="xl">
                    {END_RATE}
                  </StatNumber>
                  <StatHelpText color={getColor("text.secondary", colorMode)} fontSize="xs">tokens/USDC</StatHelpText>
                </Stat>
              </Box>
              <Box
                p={4}
                bg={colorMode === "dark" ? "rgba(139, 92, 246, 0.1)" : "purple.50"}
                borderRadius="lg"
                border="1px solid"
                borderColor="#8b5cf6"
                textAlign="center"
              >
                <Stat size="sm">
                  <StatLabel color={getColor("text.secondary", colorMode)} fontSize="xs">Total Supply</StatLabel>
                  <StatNumber color="#8b5cf6" fontSize="xl">
                    52M
                  </StatNumber>
                  <StatHelpText color={getColor("text.secondary", colorMode)} fontSize="xs">RIO tokens</StatHelpText>
                </Stat>
              </Box>
            </SimpleGrid>

            {/* Progress Bar */}
            <Box
              p={6}
              borderRadius="xl"
              bg={colorMode === "dark" ? "rgba(31, 41, 55, 0.5)" : "white"}
              border="1px solid"
              borderColor={getColor("border.medium", colorMode)}
            >
              <HStack justify="space-between" mb={2}>
                <Text fontWeight="medium" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                  Sale Progress
                </Text>
                <Text color={getColor("text.secondary", colorMode)} fontSize="sm">
                  {tokensSold.toLocaleString()} / {TOTAL_SUPPLY.toLocaleString()} tokens
                </Text>
              </HStack>
              <Progress
                value={saleProgress}
                size="lg"
                borderRadius="full"
                bg={colorMode === "dark" ? "gray.700" : "gray.200"}
                sx={{
                  "& > div": {
                    bgGradient: "linear(to-r, #10b981, #3b82f6, #8b5cf6)",
                  },
                }}
              />
              <HStack justify="space-between" mt={2}>
                <Text color="#10b981" fontSize="sm" fontWeight="medium">
                  1.4 tokens/USDC
                </Text>
                <Text color={getColor("text.secondary", colorMode)} fontSize="sm">
                  {saleProgress.toFixed(1)}% sold
                </Text>
                <Text color="#ef4444" fontSize="sm" fontWeight="medium">
                  0.6 tokens/USDC
                </Text>
              </HStack>
            </Box>

            {/* RIO Token Notice */}
            <Box
              p={6}
              borderRadius="xl"
              bg={colorMode === "dark" ? "rgba(59, 130, 246, 0.08)" : "blue.50"}
              border="1px solid"
              borderColor="#3b82f6"
            >
              <VStack align="start" spacing={4}>
                <HStack>
                  <Icon as={FaInfoCircle} color="#3b82f6" boxSize={5} />
                  <Heading size="md" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                    Why RIO Tokens First? A Smart Contract Reality
                  </Heading>
                </HStack>

                <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                  <strong>Smart contracts are immutable.</strong> Once deployed, a token's name, brand, and core features
                  cannot be changed. This is both a security feature and a limitation we must work with carefully.
                </Text>

                <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                  Rather than rush to deploy the final B52 token and risk discovering missing features or improvements
                  after launch, we're taking a methodical approach: <strong>RIO (Run It Once)</strong> serves as our
                  community testing phase. This allows us to:
                </Text>

                <VStack align="start" spacing={2} pl={4}>
                  <HStack align="start">
                    <Icon as={FaCheckCircle} color="#10b981" boxSize={4} mt={1} />
                    <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                      <strong>Test validator operations</strong> - Run real validator nodes and discover operational requirements
                    </Text>
                  </HStack>
                  <HStack align="start">
                    <Icon as={FaCheckCircle} color="#10b981" boxSize={4} mt={1} />
                    <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                      <strong>Test delegation mechanics</strong> - Understand how staking and rewards should work in practice
                    </Text>
                  </HStack>
                  <HStack align="start">
                    <Icon as={FaCheckCircle} color="#10b981" boxSize={4} mt={1} />
                    <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                      <strong>Gather community feedback</strong> - Learn what features matter most to our users
                    </Text>
                  </HStack>
                  <HStack align="start">
                    <Icon as={FaCheckCircle} color="#10b981" boxSize={4} mt={1} />
                    <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                      <strong>Refine tokenomics</strong> - Validate that our economic model works as intended
                    </Text>
                  </HStack>
                </VStack>

                <Divider borderColor="blue.200" />

                <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                  <strong>The path forward:</strong> Once we've validated everything with RIO, we'll deploy the official
                  B52 token with the exact features, brand, and parameters our community has helped shape. Deploying
                  twice under the same "B52" name would create confusion about which token is legitimate - that's why
                  we use a distinct test name now.
                </Text>

                <Box
                  p={4}
                  borderRadius="lg"
                  bg={colorMode === "dark" ? "rgba(16, 185, 129, 0.15)" : "green.100"}
                  border="1px solid"
                  borderColor="#10b981"
                  w="full"
                >
                  <HStack spacing={3}>
                    <Icon as={FaExchangeAlt} color="#10b981" boxSize={5} />
                    <Text fontSize="sm" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                      <strong>Your RIO tokens are real value.</strong> During the network upgrade to mainnet, all validators
                      will point to the official B52 token, and RIO holders will be migrated 1:1 to B52. Early supporters
                      get the best rates now, and transition seamlessly later.
                    </Text>
                  </HStack>
                </Box>
              </VStack>
            </Box>

            {/* Validator Requirements Card */}
            <Box
              p={6}
              borderRadius="xl"
              bgGradient={colorMode === "dark"
                ? "linear(to-br, rgba(139, 92, 246, 0.15), rgba(59, 130, 246, 0.1))"
                : "linear(to-br, purple.50, blue.50)"
              }
              border="2px solid"
              borderColor="#8b5cf6"
              boxShadow="0 0 20px rgba(139, 92, 246, 0.15)"
            >
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between" flexWrap="wrap" gap={2}>
                  <HStack>
                    <Icon as={FaServer} color="#8b5cf6" boxSize={6} />
                    <Heading size="md" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                      Why Buy Tokens? Become a Validator!
                    </Heading>
                  </HStack>
                  <Badge colorScheme="purple" fontSize="sm" px={3} py={1}>
                    Earn Network Fees
                  </Badge>
                </HStack>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {/* Validator Option */}
                  <Box
                    p={4}
                    borderRadius="lg"
                    bg={colorMode === "dark" ? "rgba(59, 130, 246, 0.15)" : "blue.100"}
                    border="1px solid"
                    borderColor="#3b82f6"
                  >
                    <HStack mb={3}>
                      <Icon as={FaServer} color="#3b82f6" />
                      <Text fontWeight="bold" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                        Run a Validator
                      </Text>
                    </HStack>
                    <VStack align="start" spacing={2} fontSize="sm" color={getColor("text.secondary", colorMode)}>
                      <HStack>
                        <Icon as={FaCheckCircle} color="#10b981" boxSize={4} />
                        <Text><strong>Requires:</strong> 1,000 RIO tokens (alpha) / 52,000 B52 (mainnet)</Text>
                      </HStack>
                      <HStack>
                        <Icon as={FaCheckCircle} color="#10b981" boxSize={4} />
                        <Text><strong>Earns:</strong> 40% of all network fees (shared among 52 validators)</Text>
                      </HStack>
                      <HStack>
                        <Icon as={FaCheckCircle} color="#10b981" boxSize={4} />
                        <Text><strong>Role:</strong> Verify games, sign blocks, secure the network</Text>
                      </HStack>
                    </VStack>
                  </Box>

                  {/* Delegator Option */}
                  <Box
                    p={4}
                    borderRadius="lg"
                    bg={colorMode === "dark" ? "rgba(100, 255, 218, 0.15)" : "teal.100"}
                    border="1px solid"
                    borderColor="#64ffda"
                  >
                    <HStack mb={3}>
                      <Icon as={FaUsers} color="#64ffda" />
                      <Text fontWeight="bold" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                        Become a Delegator
                      </Text>
                    </HStack>
                    <VStack align="start" spacing={2} fontSize="sm" color={getColor("text.secondary", colorMode)}>
                      <HStack>
                        <Icon as={FaCheckCircle} color="#10b981" boxSize={4} />
                        <Text><strong>Requires:</strong> 1,000+ RIO/B52 tokens (no hardware needed)</Text>
                      </HStack>
                      <HStack>
                        <Icon as={FaCheckCircle} color="#10b981" boxSize={4} />
                        <Text><strong>Earns:</strong> 30% of network fees (proportional to stake)</Text>
                      </HStack>
                      <HStack>
                        <Icon as={FaCheckCircle} color="#10b981" boxSize={4} />
                        <Text><strong>Role:</strong> Stake tokens with validators, earn passive income</Text>
                      </HStack>
                    </VStack>
                  </Box>
                </SimpleGrid>

                {/* Pendulum Note */}
                <Box
                  p={3}
                  borderRadius="md"
                  bg={colorMode === "dark" ? "rgba(245, 158, 11, 0.15)" : "orange.100"}
                  border="1px solid"
                  borderColor="#f59e0b"
                >
                  <HStack spacing={3}>
                    <Icon as={FaBalanceScale} color="#f59e0b" boxSize={5} />
                    <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                      <strong>The Incentive Pendulum:</strong> Rewards automatically shift between validators and delegators
                      based on network health. Check the tokenomics page to see current reward distribution!
                    </Text>
                  </HStack>
                </Box>

                {/* Action Buttons */}
                <HStack spacing={4} flexWrap="wrap">
                  <Button
                    as={RouterLink}
                    to="/tokenomics"
                    size="md"
                    variant="outline"
                    borderColor="#8b5cf6"
                    color="#8b5cf6"
                    _hover={{ bg: "rgba(139, 92, 246, 0.1)" }}
                    leftIcon={<FaChartLine />}
                  >
                    View Full Tokenomics
                  </Button>
                  <Button
                    as={RouterLink}
                    to="/validator-delegator"
                    size="md"
                    variant="outline"
                    borderColor="#f59e0b"
                    color="#f59e0b"
                    _hover={{ bg: "rgba(245, 158, 11, 0.1)" }}
                    leftIcon={<FaBalanceScale />}
                  >
                    Validator vs Delegator Calculator
                  </Button>
                </HStack>
              </VStack>
            </Box>

            {/* Scarcity Simulator - Interactive Slider */}
            <Box
              p={6}
              borderRadius="xl"
              bgGradient={colorMode === "dark"
                ? "linear(to-br, rgba(239, 68, 68, 0.1), rgba(245, 158, 11, 0.1), rgba(16, 185, 129, 0.1))"
                : "linear(to-br, red.50, orange.50, green.50)"
              }
              border="2px solid"
              borderColor="#f59e0b"
              boxShadow="0 0 30px rgba(245, 158, 11, 0.2)"
            >
              <VStack spacing={6} align="stretch">
                <HStack justify="space-between">
                  <HStack>
                    <Icon as={FaFire} color="#ef4444" boxSize={6} />
                    <Heading size="md" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                      Price Simulator - See How Waiting Costs You!
                    </Heading>
                  </HStack>
                  <Badge colorScheme="red" fontSize="sm" px={3} py={1}>
                    <HStack spacing={1}>
                      <Icon as={FaClock} />
                      <Text>Don't Wait!</Text>
                    </HStack>
                  </Badge>
                </HStack>

                <Text color={getColor("text.secondary", colorMode)}>
                  Drag the slider to see how your purchase power decreases as more tokens are sold.
                  <Text as="span" color="#ef4444" fontWeight="bold"> Early buyers get up to 133% more tokens!</Text>
                </Text>

                {/* Slider */}
                <Box pt={10} pb={4} px={4}>
                  <Slider
                    aria-label="tokens-sold-slider"
                    value={tokensSold}
                    min={0}
                    max={TOTAL_SUPPLY}
                    step={1000000}
                    onChange={(val) => setTokensSold(val)}
                  >
                    <SliderMark value={0} mt={4} ml={-2} fontSize="xs" color={getColor("text.secondary", colorMode)}>
                      0%
                    </SliderMark>
                    <SliderMark value={13000000} mt={4} ml={-2} fontSize="xs" color={getColor("text.secondary", colorMode)}>
                      25%
                    </SliderMark>
                    <SliderMark value={26000000} mt={4} ml={-2} fontSize="xs" color={getColor("text.secondary", colorMode)}>
                      50%
                    </SliderMark>
                    <SliderMark value={39000000} mt={4} ml={-2} fontSize="xs" color={getColor("text.secondary", colorMode)}>
                      75%
                    </SliderMark>
                    <SliderMark value={52000000} mt={4} ml={-3} fontSize="xs" color={getColor("text.secondary", colorMode)}>
                      100%
                    </SliderMark>
                    <SliderMark
                      value={tokensSold}
                      textAlign="center"
                      bg="#64ffda"
                      color="black"
                      mt={-10}
                      ml={-8}
                      w={16}
                      fontSize="xs"
                      fontWeight="bold"
                      borderRadius="md"
                      p={1}
                    >
                      {((tokensSold / TOTAL_SUPPLY) * 100).toFixed(0)}% sold
                    </SliderMark>
                    <SliderTrack bg="gray.300" h={3} borderRadius="full">
                      <SliderFilledTrack
                        bgGradient="linear(to-r, #10b981, #f59e0b, #ef4444)"
                      />
                    </SliderTrack>
                    <SliderThumb boxSize={6} bg="#64ffda" borderWidth={2} borderColor="white">
                      <Box color="#064e3b" as={FaCoins} />
                    </SliderThumb>
                  </Slider>
                </Box>

                {/* Comparison Cards */}
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={4}>
                  {/* What you get NOW */}
                  <Box
                    p={4}
                    borderRadius="lg"
                    bg={colorMode === "dark" ? "rgba(16, 185, 129, 0.2)" : "green.100"}
                    border="2px solid"
                    borderColor="#10b981"
                  >
                    <VStack spacing={2}>
                      <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                        Current Rate
                      </Text>
                      <Text fontSize="3xl" fontWeight="bold" color="#10b981">
                        {currentRate.toFixed(2)}
                      </Text>
                      <Text fontSize="xs" color={getColor("text.secondary", colorMode)}>
                        tokens per USDC
                      </Text>
                      <Divider borderColor="green.300" />
                      <Text fontSize="sm" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                        $1,000 gets you
                      </Text>
                      <Text fontSize="2xl" fontWeight="bold" color="#10b981">
                        {(currentRate * 1000).toLocaleString()}
                      </Text>
                      <Text fontSize="xs" color={getColor("text.secondary", colorMode)}>RIO tokens</Text>
                    </VStack>
                  </Box>

                  {/* Loss comparison */}
                  <Box
                    p={4}
                    borderRadius="lg"
                    bg={colorMode === "dark" ? "rgba(239, 68, 68, 0.2)" : "red.100"}
                    border="2px solid"
                    borderColor="#ef4444"
                  >
                    <VStack spacing={2}>
                      <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                        If You Wait Until End
                      </Text>
                      <Text fontSize="3xl" fontWeight="bold" color="#ef4444">
                        {END_RATE.toFixed(2)}
                      </Text>
                      <Text fontSize="xs" color={getColor("text.secondary", colorMode)}>
                        tokens per USDC
                      </Text>
                      <Divider borderColor="red.300" />
                      <Text fontSize="sm" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                        $1,000 gets you
                      </Text>
                      <Text fontSize="2xl" fontWeight="bold" color="#ef4444">
                        {(END_RATE * 1000).toLocaleString()}
                      </Text>
                      <Text fontSize="xs" color={getColor("text.secondary", colorMode)}>RIO tokens</Text>
                    </VStack>
                  </Box>

                  {/* You save / lose */}
                  <Box
                    p={4}
                    borderRadius="lg"
                    bg={colorMode === "dark" ? "rgba(245, 158, 11, 0.2)" : "orange.100"}
                    border="2px solid"
                    borderColor="#f59e0b"
                  >
                    <VStack spacing={2}>
                      <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                        Buying NOW Saves You
                      </Text>
                      <Text fontSize="3xl" fontWeight="bold" color="#f59e0b">
                        +{(((currentRate - END_RATE) / END_RATE) * 100).toFixed(0)}%
                      </Text>
                      <Text fontSize="xs" color={getColor("text.secondary", colorMode)}>
                        more tokens
                      </Text>
                      <Divider borderColor="orange.300" />
                      <Text fontSize="sm" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                        Extra tokens on $1,000
                      </Text>
                      <Text fontSize="2xl" fontWeight="bold" color="#f59e0b">
                        +{((currentRate - END_RATE) * 1000).toLocaleString()}
                      </Text>
                      <Text fontSize="xs" color={getColor("text.secondary", colorMode)}>bonus tokens</Text>
                    </VStack>
                  </Box>
                </SimpleGrid>

                {/* Urgency Message */}
                <Box
                  p={4}
                  borderRadius="lg"
                  bgGradient="linear(to-r, #ef4444, #f59e0b)"
                  color="white"
                  textAlign="center"
                >
                  <HStack justify="center" spacing={3}>
                    <Icon as={FaFire} />
                    <Text fontWeight="bold">
                      {saleProgress < 25
                        ? "Best prices available NOW! First 25% of buyers get the best rates."
                        : saleProgress < 50
                        ? "Still great rates! But prices are rising as tokens sell."
                        : saleProgress < 75
                        ? "Prices increasing rapidly! Don't miss out on remaining tokens."
                        : "Final tokens! Last chance to get RIO before the price hits bottom."}
                    </Text>
                    <Icon as={FaFire} />
                  </HStack>
                </Box>
              </VStack>
            </Box>

            {/* Main Content Tabs */}
            <Tabs colorScheme="teal" variant="enclosed">
              <TabList>
                <Tab _selected={{ color: "#64ffda", borderColor: "#64ffda", borderBottomColor: "transparent" }}>
                  <Icon as={FaCoins} mr={2} /> Buy Tokens
                </Tab>
                <Tab _selected={{ color: "#64ffda", borderColor: "#64ffda", borderBottomColor: "transparent" }}>
                  <Icon as={FaChartLine} mr={2} /> Price Curve
                </Tab>
                <Tab _selected={{ color: "#64ffda", borderColor: "#64ffda", borderBottomColor: "transparent" }}>
                  <Icon as={FaInfoCircle} mr={2} /> How It Works
                </Tab>
              </TabList>

              <TabPanels>
                {/* Buy Tokens Tab */}
                <TabPanel p={0} pt={6}>
                  <VStack spacing={6}>
                    {/* Calculator Card */}
                    <Box
                      p={6}
                      borderRadius="xl"
                      bg={colorMode === "dark" ? "rgba(31, 41, 55, 0.5)" : "white"}
                      border="2px solid"
                      borderColor="#64ffda"
                      boxShadow="0 0 20px rgba(100, 255, 218, 0.1)"
                      w="full"
                    >
                      <VStack spacing={5} align="stretch">
                        <Heading size="md" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                          Token Calculator
                        </Heading>

                        <SimpleGrid columns={2} spacing={4}>
                          {/* USDC Input */}
                          <VStack align="stretch">
                            <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                              You Send (USDC)
                            </Text>
                            <InputGroup size="lg">
                              <InputLeftElement pointerEvents="none" h="full">
                                <Text color="#3b82f6" fontWeight="bold">$</Text>
                              </InputLeftElement>
                              <Input
                                type="number"
                                placeholder="0.00"
                                value={usdcAmount}
                                onChange={(e) => {
                                  setUsdcAmount(e.target.value);
                                  setInputMode("usdc");
                                }}
                                borderColor={inputMode === "usdc" ? "#64ffda" : getColor("border.medium", colorMode)}
                                _focus={{
                                  borderColor: "#64ffda",
                                  boxShadow: "0 0 0 1px #64ffda",
                                }}
                                fontSize="xl"
                                fontWeight="bold"
                                color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}
                              />
                            </InputGroup>
                          </VStack>

                          {/* Tokens Output */}
                          <VStack align="stretch">
                            <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                              You Receive (RIO)
                            </Text>
                            <InputGroup size="lg">
                              <InputLeftElement pointerEvents="none" h="full">
                                <Icon as={GiTwoCoins} color="#64ffda" boxSize={5} />
                              </InputLeftElement>
                              <Input
                                type="number"
                                placeholder="0.00"
                                value={inputMode === "usdc" ? (calculatedTokens > 0 ? calculatedTokens.toFixed(2) : "") : tokenAmount}
                                onChange={(e) => {
                                  setTokenAmount(e.target.value);
                                  setInputMode("tokens");
                                }}
                                borderColor={inputMode === "tokens" ? "#64ffda" : getColor("border.medium", colorMode)}
                                _focus={{
                                  borderColor: "#64ffda",
                                  boxShadow: "0 0 0 1px #64ffda",
                                }}
                                fontSize="xl"
                                fontWeight="bold"
                                color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}
                              />
                            </InputGroup>
                          </VStack>
                        </SimpleGrid>

                        {/* Rate Display */}
                        <Box
                          p={3}
                          borderRadius="lg"
                          bg={colorMode === "dark" ? "rgba(100, 255, 218, 0.1)" : "teal.50"}
                          border="1px solid"
                          borderColor="rgba(100, 255, 218, 0.3)"
                        >
                          <HStack justify="center" spacing={2}>
                            <Icon as={FaExchangeAlt} color="#64ffda" />
                            <Text color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                              Current Rate: <Text as="span" color="#64ffda" fontWeight="bold">1 USDC = {currentRate.toFixed(4)} RIO</Text>
                            </Text>
                          </HStack>
                        </Box>

                        {/* Purchase Preview - Shows exactly what they get */}
                        {parseFloat(usdcAmount) > 0 && (
                          <Box
                            p={5}
                            borderRadius="lg"
                            bgGradient={colorMode === "dark"
                              ? "linear(to-br, rgba(16, 185, 129, 0.2), rgba(100, 255, 218, 0.1))"
                              : "linear(to-br, green.100, teal.50)"
                            }
                            border="2px solid"
                            borderColor="#10b981"
                            boxShadow="0 0 20px rgba(16, 185, 129, 0.2)"
                          >
                            <VStack spacing={3}>
                              <HStack>
                                <Icon as={FaCheckCircle} color="#10b981" boxSize={5} />
                                <Text fontWeight="bold" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                                  Purchase Preview
                                </Text>
                              </HStack>

                              <Divider borderColor="green.300" />

                              <SimpleGrid columns={2} spacing={4} w="full">
                                <VStack align="start">
                                  <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                                    You Pay
                                  </Text>
                                  <HStack>
                                    <Text fontSize="2xl" fontWeight="bold" color="#3b82f6">
                                      {parseFloat(usdcAmount).toLocaleString()}
                                    </Text>
                                    <Text color={getColor("text.secondary", colorMode)}>USDC</Text>
                                  </HStack>
                                </VStack>

                                <VStack align="end">
                                  <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                                    You Receive
                                  </Text>
                                  <HStack>
                                    <Text fontSize="2xl" fontWeight="bold" color="#10b981">
                                      {calculatedTokens.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                    </Text>
                                    <Text color={getColor("text.secondary", colorMode)}>RIO</Text>
                                  </HStack>
                                </VStack>
                              </SimpleGrid>

                              <Divider borderColor="green.300" />

                              <HStack justify="space-between" w="full" fontSize="sm">
                                <Text color={getColor("text.secondary", colorMode)}>
                                  Rate Applied:
                                </Text>
                                <Text color="#64ffda" fontWeight="bold">
                                  {currentRate.toFixed(4)} tokens/USDC
                                </Text>
                              </HStack>

                              <HStack justify="space-between" w="full" fontSize="sm">
                                <Text color={getColor("text.secondary", colorMode)}>
                                  Bonus vs. End Rate:
                                </Text>
                                <Text color="#f59e0b" fontWeight="bold">
                                  +{((calculatedTokens - (parseFloat(usdcAmount) * END_RATE))).toLocaleString(undefined, { maximumFractionDigits: 0 })} extra tokens
                                </Text>
                              </HStack>

                              <Box
                                p={3}
                                borderRadius="md"
                                bg="rgba(16, 185, 129, 0.2)"
                                w="full"
                                textAlign="center"
                              >
                                <Text fontSize="sm" color="#10b981" fontWeight="bold">
                                  Buying now saves you {(((currentRate - END_RATE) / END_RATE) * 100).toFixed(0)}% compared to waiting!
                                </Text>
                              </Box>
                            </VStack>
                          </Box>
                        )}
                      </VStack>
                    </Box>

                    {/* Treasury Address Card */}
                    <Box
                      p={6}
                      borderRadius="xl"
                      bg={colorMode === "dark" ? "rgba(31, 41, 55, 0.5)" : "white"}
                      border="1px solid"
                      borderColor={getColor("border.medium", colorMode)}
                      w="full"
                    >
                      <VStack spacing={4} align="stretch">
                        <HStack>
                          <Icon as={FaWallet} color="#3b82f6" />
                          <Heading size="md" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                            Send USDC to Treasury
                          </Heading>
                        </HStack>

                        <Text color={getColor("text.secondary", colorMode)} fontSize="sm">
                          Send USDC (on Ethereum) to the treasury address below. After your transfer is confirmed,
                          we will send your RIO tokens to your wallet within 24 hours.
                        </Text>

                        <Box
                          p={4}
                          borderRadius="lg"
                          bg={colorMode === "dark" ? "rgba(59, 130, 246, 0.1)" : "blue.50"}
                          border="1px solid"
                          borderColor="#3b82f6"
                        >
                          <VStack spacing={3} align="stretch">
                            <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                              Treasury Address (Ethereum)
                            </Text>
                            <HStack>
                              <Code
                                p={3}
                                borderRadius="md"
                                bg={colorMode === "dark" ? "gray.800" : "gray.100"}
                                color="#3b82f6"
                                fontSize={{ base: "xs", md: "sm" }}
                                flex={1}
                                wordBreak="break-all"
                              >
                                {TREASURY_ADDRESS}
                              </Code>
                              <Tooltip label={copied ? "Copied!" : "Copy Address"}>
                                <IconButton
                                  aria-label="Copy address"
                                  icon={copied ? <FaCheckCircle /> : <FaCopy />}
                                  colorScheme={copied ? "green" : "blue"}
                                  variant="outline"
                                  onClick={copyAddress}
                                />
                              </Tooltip>
                            </HStack>
                          </VStack>
                        </Box>

                        <Alert status="warning" borderRadius="lg">
                          <AlertIcon />
                          <Text fontSize="sm">
                            Only send USDC on the <strong>Ethereum network</strong>. Tokens sent on other networks may be lost.
                          </Text>
                        </Alert>

                        <Divider />

                        <VStack spacing={2} align="stretch">
                          <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                            Contract Addresses:
                          </Text>
                          <HStack justify="space-between" fontSize="xs">
                            <Text color={getColor("text.secondary", colorMode)}>RIO Token:</Text>
                            <Code fontSize="xs" bg="transparent" color="#64ffda">
                              {RIO_TOKEN_ADDRESS}
                            </Code>
                          </HStack>
                          <HStack justify="space-between" fontSize="xs">
                            <Text color={getColor("text.secondary", colorMode)}>USDC (Base):</Text>
                            <Code fontSize="xs" bg="transparent" color="#3b82f6">
                              {USDC_BASE_ADDRESS}
                            </Code>
                          </HStack>
                        </VStack>
                      </VStack>
                    </Box>
                  </VStack>
                </TabPanel>

                {/* Price Curve Tab */}
                <TabPanel p={0} pt={6}>
                  <Box
                    p={6}
                    borderRadius="xl"
                    bg={colorMode === "dark" ? "rgba(31, 41, 55, 0.5)" : "white"}
                    border="1px solid"
                    borderColor={getColor("border.medium", colorMode)}
                  >
                    <VStack spacing={6} align="stretch">
                      <Heading size="md" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                        Sliding Scale Pricing
                      </Heading>

                      <Text color={getColor("text.secondary", colorMode)}>
                        Early buyers get more tokens per USDC. As more tokens are sold, the rate decreases
                        from {START_RATE} tokens/USDC down to {END_RATE} tokens/USDC.
                      </Text>

                      <Box
                        p={4}
                        borderRadius="lg"
                        bg={colorMode === "dark" ? "rgba(100, 255, 218, 0.05)" : "teal.50"}
                        fontFamily="mono"
                        fontSize="sm"
                        color={getColor("text.secondary", colorMode)}
                      >
                        <Text color="#64ffda" fontWeight="bold" mb={2}>Formula:</Text>
                        <Text>rate = 1.4 - (0.8 * tokens_sold / 52,000,000)</Text>
                      </Box>

                      <TableContainer>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th color={getColor("text.secondary", colorMode)}>Stage</Th>
                              <Th color={getColor("text.secondary", colorMode)} isNumeric>Tokens Sold</Th>
                              <Th color={getColor("text.secondary", colorMode)} isNumeric>Rate (tokens/USDC)</Th>
                              <Th color={getColor("text.secondary", colorMode)} isNumeric>$1000 Gets</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {priceExamples.map((example, index) => (
                              <Tr key={index}>
                                <Td color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>{example.label}</Td>
                                <Td isNumeric color={getColor("text.secondary", colorMode)}>
                                  {(example.sold / 1_000_000).toFixed(0)}M
                                </Td>
                                <Td isNumeric>
                                  <Text
                                    color={
                                      example.rate >= 1.2 ? "#10b981" :
                                      example.rate >= 0.9 ? "#f59e0b" :
                                      "#ef4444"
                                    }
                                    fontWeight="bold"
                                  >
                                    {example.rate.toFixed(2)}
                                  </Text>
                                </Td>
                                <Td isNumeric color="#64ffda" fontWeight="bold">
                                  {(example.rate * 1000).toLocaleString()} tokens
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>

                      {/* Visual Price Curve */}
                      <Box
                        h="200px"
                        borderRadius="lg"
                        bg={colorMode === "dark" ? "gray.800" : "gray.50"}
                        p={4}
                        position="relative"
                        overflow="hidden"
                      >
                        <Text
                          position="absolute"
                          top={2}
                          left={2}
                          fontSize="xs"
                          color={getColor("text.secondary", colorMode)}
                        >
                          Tokens/USDC
                        </Text>
                        <Text
                          position="absolute"
                          bottom={2}
                          right={2}
                          fontSize="xs"
                          color={getColor("text.secondary", colorMode)}
                        >
                          Tokens Sold
                        </Text>

                        {/* Y-axis labels */}
                        <VStack
                          position="absolute"
                          left={2}
                          top="20%"
                          bottom="20%"
                          justify="space-between"
                          fontSize="xs"
                          color={getColor("text.secondary", colorMode)}
                        >
                          <Text>1.4</Text>
                          <Text>1.0</Text>
                          <Text>0.6</Text>
                        </VStack>

                        {/* Curve visualization */}
                        <Box
                          position="absolute"
                          left="40px"
                          right="20px"
                          top="20%"
                          bottom="20%"
                        >
                          <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                            <defs>
                              <linearGradient id="curveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#10b981" />
                                <stop offset="50%" stopColor="#3b82f6" />
                                <stop offset="100%" stopColor="#ef4444" />
                              </linearGradient>
                            </defs>
                            {/* Background area */}
                            <path
                              d="M 0 0 L 0 100 L 100 100 L 100 0 Q 50 50 0 0"
                              fill="url(#curveGradient)"
                              fillOpacity="0.1"
                            />
                            {/* Line curve */}
                            <path
                              d="M 0 0 Q 50 50 100 100"
                              fill="none"
                              stroke="url(#curveGradient)"
                              strokeWidth="3"
                            />
                            {/* Current position marker */}
                            <circle
                              cx={saleProgress}
                              cy={(1 - (currentRate - END_RATE) / (START_RATE - END_RATE)) * 100}
                              r="4"
                              fill="#64ffda"
                              stroke="white"
                              strokeWidth="2"
                            />
                          </svg>
                        </Box>
                      </Box>
                    </VStack>
                  </Box>
                </TabPanel>

                {/* How It Works Tab */}
                <TabPanel p={0} pt={6}>
                  <VStack spacing={6}>
                    <Box
                      p={6}
                      borderRadius="xl"
                      bg={colorMode === "dark" ? "rgba(31, 41, 55, 0.5)" : "white"}
                      border="1px solid"
                      borderColor={getColor("border.medium", colorMode)}
                      w="full"
                    >
                      <VStack spacing={6} align="stretch">
                        <Heading size="md" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                          How the Token Sale Works
                        </Heading>

                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                          {/* Step 1 */}
                          <Box
                            p={4}
                            borderRadius="lg"
                            bg={colorMode === "dark" ? "rgba(16, 185, 129, 0.1)" : "green.50"}
                            border="1px solid"
                            borderColor="#10b981"
                          >
                            <HStack mb={2}>
                              <Badge colorScheme="green" borderRadius="full" px={3}>1</Badge>
                              <Text fontWeight="bold" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                                Calculate Your Amount
                              </Text>
                            </HStack>
                            <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                              Use the calculator to see how many RIO tokens you will receive for your USDC.
                              The earlier you buy, the better rate you get.
                            </Text>
                          </Box>

                          {/* Step 2 */}
                          <Box
                            p={4}
                            borderRadius="lg"
                            bg={colorMode === "dark" ? "rgba(59, 130, 246, 0.1)" : "blue.50"}
                            border="1px solid"
                            borderColor="#3b82f6"
                          >
                            <HStack mb={2}>
                              <Badge colorScheme="blue" borderRadius="full" px={3}>2</Badge>
                              <Text fontWeight="bold" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                                Send USDC
                              </Text>
                            </HStack>
                            <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                              Send USDC (on Ethereum) to the treasury address. Make sure to use the correct network
                              to avoid losing funds.
                            </Text>
                          </Box>

                          {/* Step 3 */}
                          <Box
                            p={4}
                            borderRadius="lg"
                            bg={colorMode === "dark" ? "rgba(139, 92, 246, 0.1)" : "purple.50"}
                            border="1px solid"
                            borderColor="#8b5cf6"
                          >
                            <HStack mb={2}>
                              <Badge colorScheme="purple" borderRadius="full" px={3}>3</Badge>
                              <Text fontWeight="bold" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                                Receive RIO Tokens
                              </Text>
                            </HStack>
                            <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                              Within 24 hours of your confirmed transfer, RIO tokens will be sent to your wallet address.
                            </Text>
                          </Box>

                          {/* Step 4 */}
                          <Box
                            p={4}
                            borderRadius="lg"
                            bg={colorMode === "dark" ? "rgba(245, 158, 11, 0.1)" : "orange.50"}
                            border="1px solid"
                            borderColor="#f59e0b"
                          >
                            <HStack mb={2}>
                              <Badge colorScheme="orange" borderRadius="full" px={3}>4</Badge>
                              <Text fontWeight="bold" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                                Swap for B52
                              </Text>
                            </HStack>
                            <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                              When the mainnet launches, your RIO tokens will be swapped 1:1 for real B52 tokens.
                              Early supporters may receive bonus rewards.
                            </Text>
                          </Box>
                        </SimpleGrid>

                        <Divider />

                        {/* Why RIO First */}
                        <Box>
                          <Heading size="sm" mb={3} color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                            Why RIO Tokens First?
                          </Heading>
                          <VStack align="stretch" spacing={2}>
                            <HStack>
                              <Icon as={FaCheckCircle} color="#10b981" />
                              <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                                <strong>Testing:</strong> Allows us to test the entire system before mainnet
                              </Text>
                            </HStack>
                            <HStack>
                              <Icon as={FaCheckCircle} color="#10b981" />
                              <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                                <strong>Iteration:</strong> We can improve based on community feedback
                              </Text>
                            </HStack>
                            <HStack>
                              <Icon as={FaCheckCircle} color="#10b981" />
                              <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                                <strong>Security:</strong> Identify and fix any issues before real launch
                              </Text>
                            </HStack>
                            <HStack>
                              <Icon as={FaCheckCircle} color="#10b981" />
                              <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                                <strong>Rewards:</strong> Early supporters get the best rates and potential bonuses
                              </Text>
                            </HStack>
                          </VStack>
                        </Box>
                      </VStack>
                    </Box>
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>

            {/* Info Links */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Button
                as={RouterLink}
                to="/tokenomics"
                variant="outline"
                size="lg"
                borderColor="#64ffda"
                color="#64ffda"
                _hover={{ bg: "rgba(100, 255, 218, 0.1)" }}
                leftIcon={<FaCoins />}
              >
                View Token Economics
              </Button>
              <Button
                as={RouterLink}
                to="/use/stake-b52"
                variant="outline"
                size="lg"
                borderColor="#3b82f6"
                color="#3b82f6"
                _hover={{ bg: "rgba(59, 130, 246, 0.1)" }}
                leftIcon={<FaChartLine />}
              >
                Learn About Staking
              </Button>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>
      <FooterWithFourColumns />
    </>
  );
};

export default TokenSalePage;
